import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
} from '@cca/ui';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { useRuntimeConfig } from '../lib/runtime-config.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface Campaign {
  readonly id: string;
  readonly name: string;
  readonly state: string;
  readonly communication_class: string;
  readonly version: number;
  readonly subject: string | null;
  readonly body_text: string | null;
  readonly campaign_audience_previews:
    | readonly {
        campaign_version: number;
        recipient_count: number;
        total_considered: number;
        exclusion_counts: Record<string, number>;
        sample: { email: string; displayName: string }[];
      }[]
    | null;
}

interface PreviewResponse {
  readonly recipientCount: number;
  readonly totalConsidered: number;
  readonly exclusionCounts: Record<string, number>;
  readonly sample: { email: string; displayName: string }[];
  readonly audienceHash: string;
}

interface ApproveResponse {
  readonly state: string;
  readonly recipientCount: number;
  readonly audienceHash: string;
  readonly contentHash: string;
}

const STATE_TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  IN_REVIEW: 'info',
  CHANGES_REQUESTED: 'warning',
  APPROVED: 'info',
  SCHEDULED: 'info',
  SENDING: 'warning',
  SENT: 'success',
  PARTIALLY_FAILED: 'danger',
  FAILED: 'danger',
  CANCELLED: 'neutral',
};

/** Exclusion reasons read as machine keys; staff should see sentences. */
const EXCLUSION_LABELS: Record<string, string> = {
  no_marketing_consent: 'No marketing consent',
  unsubscribed: 'Unsubscribed',
  hard_bounced: 'Hard bounced',
  spam_complaint: 'Reported as spam',
  invalid_address: 'Invalid address',
  geographic_restriction: 'Excluded country',
  wrong_contact_type: 'Wrong contact type',
  duplicate: 'Duplicate address',
  missing_required_tag: 'Missing a required tag',
  no_matching_tag: 'No matching tag',
  excluded_by_tag: 'Excluded by tag',
  has_not_purchased_required_product: 'Has not purchased the required product',
  already_purchased_excluded_product: 'Already purchased the excluded product',
  below_minimum_orders: 'Too few orders',
  no_purchase_history: 'Never ordered',
  active_too_recently: 'Ordered too recently',
  outside_target_countries: 'Outside target countries',
};

function ExclusionTable({ counts }: { readonly counts: Record<string, number> }) {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return <p className="text-xs text-slate-500 dark:text-slate-400">Nobody was excluded.</p>;
  }

  return (
    <dl className="space-y-1">
      {entries.map(([reason, count]) => (
        <div key={reason} className="flex justify-between gap-3 text-xs">
          <dt className="text-slate-600 dark:text-slate-400">
            {EXCLUSION_LABELS[reason] ?? reason.replaceAll('_', ' ')}
          </dt>
          <dd className="font-medium text-slate-800 dark:text-slate-200">{count}</dd>
        </div>
      ))}
    </dl>
  );
}

function CampaignCard({
  campaign,
  accessToken,
  onChanged,
}: {
  readonly campaign: Campaign;
  readonly accessToken: string | null;
  readonly onChanged: () => void;
}) {
  const config = useRuntimeConfig();
  const [approved, setApproved] = useState<ApproveResponse | null>(null);

  const preview = useMutation({
    mutationFn: () =>
      apiRequest<PreviewResponse>(`/api/campaigns/${campaign.id}/preview`, {
        method: 'POST',
        accessToken,
      }),
  });

  const approve = useMutation({
    mutationFn: () =>
      apiRequest<ApproveResponse>(`/api/campaigns/${campaign.id}/approve`, {
        method: 'POST',
        accessToken,
      }),
    onSuccess: (data) => {
      setApproved(data);
      onChanged();
    },
  });

  const sendBatch = useMutation({
    mutationFn: (hashes: { audienceHash: string; contentHash: string }) =>
      apiRequest<{
        processed: number;
        sent: number;
        failed: number;
        suppressed: number;
        remaining: number;
        campaignState: string;
      }>(`/api/campaigns/${campaign.id}/send-batch`, {
        method: 'POST',
        accessToken,
        body: { ...hashes, batchSize: 100 },
      }),
    onSuccess: onChanged,
  });

  const stored = campaign.campaign_audience_previews?.find(
    (p) => p.campaign_version === campaign.version,
  );
  const shown = preview.data ?? stored;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{campaign.name}</CardTitle>
          <CardDescription>
            {campaign.communication_class.toLowerCase()} · version {campaign.version}
          </CardDescription>
        </div>
        <Badge tone={STATE_TONE[campaign.state] ?? 'neutral'}>
          {campaign.state.toLowerCase().replaceAll('_', ' ')}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-3">
        {campaign.subject ? (
          <p className="text-sm">
            <span className="text-slate-500 dark:text-slate-400">Subject: </span>
            {campaign.subject}
          </p>
        ) : null}

        {campaign.body_text ? (
          <p className="rounded-md border border-slate-200 bg-white p-3 text-sm whitespace-pre-wrap text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
            {campaign.body_text}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={preview.isPending}
            onClick={() => preview.mutate()}
          >
            {preview.isPending ? 'Calculating…' : 'Calculate audience'}
          </Button>

          {campaign.state === 'DRAFT' || campaign.state === 'IN_REVIEW' ? (
            <Button size="sm" disabled={approve.isPending} onClick={() => approve.mutate()}>
              {approve.isPending ? 'Approving…' : 'Approve and freeze audience'}
            </Button>
          ) : null}

          {approved || campaign.state === 'APPROVED' || campaign.state === 'SENDING' ? (
            <Button
              size="sm"
              variant="danger"
              disabled={sendBatch.isPending || !approved}
              title={
                approved
                  ? undefined
                  : 'Approve in this session to obtain the hashes the send is bound to.'
              }
              onClick={() =>
                approved &&
                sendBatch.mutate({
                  audienceHash: approved.audienceHash,
                  contentHash: approved.contentHash,
                })
              }
            >
              {sendBatch.isPending ? 'Sending…' : 'Send next batch'}
            </Button>
          ) : null}
        </div>

        {shown ? (
          <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-950">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {'recipientCount' in shown ? shown.recipientCount : shown.recipient_count} recipients
              <span className="font-normal text-slate-500 dark:text-slate-400">
                {' '}
                of {'totalConsidered' in shown
                  ? shown.totalConsidered
                  : shown.total_considered}{' '}
                considered
              </span>
            </p>

            <div className="mt-2">
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Why the rest were excluded
              </p>
              <div className="mt-1">
                <ExclusionTable
                  counts={
                    'exclusionCounts' in shown ? shown.exclusionCounts : shown.exclusion_counts
                  }
                />
              </div>
            </div>

            {shown.sample.length > 0 ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Sample: {shown.sample.map((s) => s.email).join(', ')}
              </p>
            ) : null}
          </div>
        ) : null}

        {approve.isSuccess && approved ? (
          <p role="status" className="text-sm text-emerald-800 dark:text-emerald-300">
            Approved. {approved.recipientCount} recipients frozen — later changes to a contact do
            not join this send.
          </p>
        ) : null}

        {sendBatch.isSuccess ? (
          <p role="status" className="text-sm text-emerald-800 dark:text-emerald-300">
            Sent {sendBatch.data.sent}, failed {sendBatch.data.failed}, skipped as suppressed{' '}
            {sendBatch.data.suppressed}. {sendBatch.data.remaining} remaining.
            {config.simulatorEnabled ? ' Nothing left the platform.' : ''}
          </p>
        ) : null}

        {[preview, approve, sendBatch].map((mutation, index) =>
          mutation.isError ? (
            <p key={index} role="alert" className="text-sm text-red-700 dark:text-red-400">
              {mutation.error instanceof ApiError ? mutation.error.message : 'Unexpected error.'}
            </p>
          ) : null,
        )}
      </CardContent>
    </Card>
  );
}

function CampaignsPage() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [name, setName] = useState('Weekend promotion');

  const campaigns = useQuery({
    queryKey: ['campaigns'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<{ campaigns: Campaign[] }>('/api/campaigns', { accessToken }),
  });

  const create = useMutation({
    mutationFn: () =>
      apiRequest<{ campaignId: string }>('/api/campaigns', {
        method: 'POST',
        accessToken,
        body: {
          name,
          subject: `${name}: 20% off`,
          bodyText: 'Use code SUMMER20 between 8 and 10 August.',
          communicationClass: 'MARKETING',
          rule: { audienceType: 'CUSTOMERS' },
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Campaigns</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          The audience is calculated from an explicit rule and frozen at approval, so the list that
          was approved is the list that sends.
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1 space-y-1">
            <label htmlFor="campaign-name" className="block text-sm font-medium">
              New campaign
            </label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <Button disabled={create.isPending || name.trim() === ''} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : 'Create draft'}
          </Button>
        </CardContent>
      </Card>

      {campaigns.isPending ? <LoadingState label="Loading campaigns" /> : null}

      {campaigns.isError ? (
        <ErrorState
          description={
            campaigns.error instanceof ApiError ? campaigns.error.message : 'Could not load.'
          }
          onRetry={() => void campaigns.refetch()}
        />
      ) : null}

      {campaigns.data && campaigns.data.campaigns.length === 0 ? (
        <Card>
          <EmptyState title="No campaigns" description="Create a draft above to get started." />
        </Card>
      ) : null}

      <ul className="space-y-3">
        {(campaigns.data?.campaigns ?? []).map((campaign) => (
          <li key={campaign.id}>
            <CampaignCard
              campaign={campaign}
              accessToken={accessToken}
              onChanged={() => void queryClient.invalidateQueries({ queryKey: ['campaigns'] })}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export const campaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/campaigns',
  component: () => (
    <RequireSession>
      <CampaignsPage />
    </RequireSession>
  ),
});
